import React from "react";
import { Alert, Button, Divider, Modal, Popconfirm, Skeleton, Tag } from "antd";
import { LinearProgress } from "@mui/material";

import styles from "./StaffDetailsModal.module.css";
import { hasMultiRoleAssignment, MULTI_ROLE_ASSIGNMENT_HINT } from '../../../utils/staffPositions';

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatArea = (value) => {
  const number = toFiniteNumber(value);
  return number === null ? "Not available" : `${number.toLocaleString()} sq. ft`;
};

const formatPayRate = (value) => {
  const number = toFiniteNumber(value);
  return number === null
    ? "Not available"
    : `$${number.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const displayValue = (value) => (
  value === null || value === undefined || value === "" ? "Not available" : value
);

const formatPlow = (plow) => {
  if (!plow) return "Not assigned";
  return [plow.plow_model, plow.plow_name].filter(Boolean).join(" - ") || `Plow ${plow.plow_id}`;
};

const formatDoubleDown = (doubleDown) => {
  if (!doubleDown) return "Not assigned";
  return [doubleDown.doubleDown_name, doubleDown.doubleDown_model, doubleDown.doubleDown_type_name]
    .filter(Boolean)
    .join(" · ") || `Double Down ${doubleDown.doubleDown_id}`;
};

const statusColor = (status) => {
  if (status === "Confirmed") return "green";
  if (status === "Unconfirmed") return "gold";
  return "default";
};

const StaffDetailsModal = ({
  open,
  staff,
  assignedSites = [],
  staffStatus,
  sitesStatus,
  setupName,
  refreshWarning,
  onClose,
  onRetryStaff,
  onRetrySites,
  onRetryAll,
  onDeallocate,
  isDeallocating,
  onSwap,
  isSwapping = false,
  readOnly = false,
}) => {
  if (!open) return null;

  const loader = staff?.assigned_loader || null;
  const assignmentInconsistent = staffStatus === "ready" && Boolean(staff?.assigned_bool) !== Boolean(loader);
  const lockedSites = assignedSites.filter((site) => !site.archived && site.staff_allocation_finalized);
  const isSiteDataCurrent = sitesStatus === "ready";
  const canDeallocate = Boolean(loader)
    && staffStatus === "ready"
    && isSiteDataCurrent
    && !assignmentInconsistent
    && lockedSites.length === 0
    && !isDeallocating
    && !isSwapping
    && !readOnly;
  const canSwap = canDeallocate
    && Boolean(onSwap)
    && !staff?.archived
    && !loader?.archived;

  const usedCapacity = assignedSites.reduce(
    (total, site) => total + (toFiniteNumber(site.allocated_capacity) || 0),
    0,
  );
  const maxCapacity = toFiniteNumber(loader?.vehicle_capacity) || 0;
  const remainingCapacity = Math.max(maxCapacity - usedCapacity, 0);
  const overCapacity = Math.max(usedCapacity - maxCapacity, 0);
  const capacityMismatch = maxCapacity === 0 && usedCapacity > 0;
  const allocationPercentage = maxCapacity > 0 ? (usedCapacity / maxCapacity) * 100 : 0;
  const progressValue = capacityMismatch ? 100 : Math.min(Math.max(allocationPercentage, 0), 100);
  const allSitesOverallocated = assignedSites.length > 0
    && usedCapacity === 0
    && assignedSites.every((site) => Boolean(site.overallocated_to_site));
  const loaderSubtitle = loader
    ? [loader.vehicle_type, loader.vehicle_subtype, loader.vehicle_make, loader.vehicle_model, loader.vehicle_year]
      .filter(Boolean)
      .join(" · ")
    : "";
  const staffSubtitle = staff
    ? [staff.position, staff.contract_type, staff.city].filter(Boolean).join(" · ")
    : "";

  const lockExplanation = lockedSites.length === 1
    ? `Staff allocation is finalized for ${lockedSites[0].site_name || "this site"}. Unlock it from the site row to make changes.`
    : lockedSites.length > 1
      ? `Staff allocation is finalized for ${lockedSites[0].site_name || "one site"} and ${lockedSites.length - 1} additional ${lockedSites.length === 2 ? "site" : "sites"}. Unlock them from the site rows to make changes.`
      : null;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={920}
      destroyOnClose
      className={styles.modal}
      title={(
        <div className={styles.header}>
          <span className={styles.eyebrow}>Staff Details</span>
          <div className={styles.titleRow}>
            <div className={styles.titleContent}>
              <h2 title={staff?.name || "Staff Details"}>{staff?.name || "Staff Details"}</h2>
              {staffSubtitle && <p title={staffSubtitle}>{staffSubtitle}</p>}
            </div>
            {staff && (
              <div className={styles.tags} aria-label="Staff statuses">
                {staff.archived && <Tag>Unavailable</Tag>}
                <Tag color={statusColor(staff.status)}>{staff.status || "Status unavailable"}</Tag>
                <Tag color={staff.assigned_bool || hasMultiRoleAssignment(staff) ? "green" : "default"}
                  title={hasMultiRoleAssignment(staff) ? MULTI_ROLE_ASSIGNMENT_HINT : undefined}>
                  {staff.assigned_bool ? "Assigned" : hasMultiRoleAssignment(staff) ? "Assigned elsewhere" : "Assignable"}
                </Tag>
              </div>
            )}
          </div>
        </div>
      )}
    >
      {staffStatus === "loading" || staffStatus === "idle" ? (
        <Skeleton active aria-label="Loading staff details" />
      ) : staffStatus === "error" ? (
        <Alert
          type="error"
          showIcon
          message="Staff details couldn't be loaded."
          action={<Button onClick={onRetryStaff}>Retry</Button>}
        />
      ) : staff ? (
        <div className={styles.content}>
          {refreshWarning && (
            <Alert
              type="warning"
              showIcon
              message={refreshWarning}
              action={<Button onClick={onRetryAll}>Retry data</Button>}
            />
          )}
          {assignmentInconsistent && (
            <Alert
              type="warning"
              showIcon
              message="The staff assignment details are inconsistent. Refresh before making changes."
              action={<Button onClick={onRetryStaff}>Retry</Button>}
            />
          )}

          <section aria-labelledby="staff-overview-heading">
            <h3 id="staff-overview-heading">Staff Overview</h3>
            <dl className={styles.overviewGrid}>
              <div><dt>Position</dt><dd title={displayValue(staff.position)}>{displayValue(staff.position)}</dd></div>
              <div><dt>City</dt><dd title={displayValue(staff.city)}>{displayValue(staff.city)}</dd></div>
              <div><dt>Contract type</dt><dd title={displayValue(staff.contract_type)}>{displayValue(staff.contract_type)}</dd></div>
              <div><dt>Pay rate</dt><dd>{formatPayRate(staff.pay_rate)}</dd></div>
              <div><dt>Staff rating</dt><dd>{displayValue(staff.staff_rating)}</dd></div>
              <div><dt>Overall rating</dt><dd>{displayValue(staff.staff_overall_rating)}</dd></div>
            </dl>
          </section>

          <Divider />

          <section aria-labelledby="loader-assignment-heading">
            <div className={styles.sectionHeading}>
              <h3 id="loader-assignment-heading">Loader Assignment</h3>
              {loader && <span>{loader.archived ? "Retired loader" : "Active fleet"}</span>}
            </div>
            {!loader && !assignmentInconsistent ? (
              <p className={styles.emptyState}>No loader is assigned. Use the existing drag-and-drop workflow to create an assignment.</p>
            ) : loader ? (
              <>
                {loader.archived && (
                  <Alert type="warning" showIcon message="This loader is no longer in the active fleet." />
                )}
                <div className={styles.loaderCard}>
                  <div className={styles.loaderIdentity}>
                    <div>
                      <strong title={loader.vehicle_name || "Unnamed loader"}>{loader.vehicle_name || "Unnamed loader"}</strong> {loader.archived && <Tag>Unavailable</Tag>}
                      <span title={loaderSubtitle || "Details unavailable"}>{loaderSubtitle || "Details unavailable"}</span>
                    </div>
                    <div className={styles.tags}>
                      <Tag color={loader.is_confirmed ? "green" : "gold"}>
                        {loader.is_confirmed ? "Vehicle confirmed" : "Vehicle unconfirmed"}
                      </Tag>
                      {assignedSites.some((site) => site.overallocated_to_site) && <Tag color="orange">Overallocated</Tag>}
                    </div>
                  </div>
                  <div className={styles.equipmentGrid}>
                    <div>
                      <span>Plow</span>
                      <strong title={formatPlow(loader.assigned_plow)}>{formatPlow(loader.assigned_plow)}</strong>
                      {loader.assigned_plow && (
                        <small>{loader.assigned_plow.is_confirmed ? "Confirmed" : "Unconfirmed"}</small>
                      )}
                    </div>
                    <div>
                      <span>Double Down</span>
                      <strong title={formatDoubleDown(loader.assigned_doubleDown)}>{formatDoubleDown(loader.assigned_doubleDown)}</strong>
                    </div>
                  </div>
                </div>
              </>
            ) : null}
          </section>

          {loader && (
            <>
              <Divider />
              <section aria-labelledby="loader-capacity-heading">
                <div className={styles.sectionHeading}>
                  <h3 id="loader-capacity-heading">Capacity</h3>
                  <span>{capacityMismatch ? "Capacity mismatch" : `${allocationPercentage.toFixed(1)}% used`}</span>
                </div>
                {sitesStatus === "loading" || sitesStatus === "idle" ? (
                  <Skeleton active paragraph={{ rows: 2 }} aria-label="Loading site allocation details" />
                ) : sitesStatus === "error" ? (
                  <Alert
                    type="error"
                    showIcon
                    message="Site allocation details couldn't be loaded."
                    action={<Button onClick={onRetrySites}>Retry</Button>}
                  />
                ) : (
                  <>
                    {capacityMismatch && (
                      <p className={styles.danger}>Existing allocation rows exceed the loader's current zero capacity.</p>
                    )}
                    {allSitesOverallocated && (
                      <p className={styles.warning}>Assigned to overallocated sites; no capacity committed.</p>
                    )}
                    <div className={styles.progressRow}>
                      <LinearProgress
                        variant="determinate"
                        value={progressValue}
                        className={styles.progress}
                        aria-label="Loader capacity used"
                        aria-valuetext={capacityMismatch ? "Capacity mismatch" : `${allocationPercentage.toFixed(1)}% used`}
                        sx={{
                          backgroundColor: "#dfe5e3",
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: capacityMismatch || overCapacity > 0
                              ? "#b42318"
                              : allocationPercentage >= 100 ? "#034832" : usedCapacity > 0 ? "#cdaf5e" : "#aeb8b5",
                          },
                        }}
                      />
                      <strong>{capacityMismatch ? "Mismatch" : `${allocationPercentage.toFixed(1)}%`}</strong>
                    </div>
                    <div className={`${styles.capacityGrid} ${overCapacity > 0 ? styles.capacityGridFour : ""}`}>
                      <div><span>Used</span><strong>{formatArea(usedCapacity)}</strong></div>
                      <div><span>Remaining</span><strong>{formatArea(remainingCapacity)}</strong></div>
                      <div><span>Maximum</span><strong>{formatArea(maxCapacity)}</strong></div>
                      {overCapacity > 0 && <div className={styles.overStat}><span>Over by</span><strong>{formatArea(overCapacity)}</strong></div>}
                    </div>
                  </>
                )}
              </section>

              <Divider />

              <section aria-labelledby="assigned-sites-heading">
                <div className={styles.sectionHeading}>
                  <h3 id="assigned-sites-heading">Assigned Sites</h3>
                  {sitesStatus === "ready" && <span>{assignedSites.length} {assignedSites.length === 1 ? "site" : "sites"}</span>}
                </div>
                {sitesStatus === "loading" || sitesStatus === "idle" ? (
                  <Skeleton active paragraph={{ rows: 3 }} />
                ) : sitesStatus === "error" ? (
                  <Alert
                    type="error"
                    showIcon
                    message="Site allocation details couldn't be loaded."
                    action={<Button onClick={onRetrySites}>Retry</Button>}
                  />
                ) : assignedSites.length > 0 ? (
                  <div className={styles.tableWrapper}>
                    <table>
                      <thead>
                        <tr>
                          <th>Client</th><th>Site</th><th>Region</th><th>Status</th><th>Site coverage</th>
                          <th>Loader capacity used</th><th>Loader finalized</th><th>Staff finalized</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assignedSites.map((site) => (
                          <tr key={`${site.site_id}-${site.vehicle_id}`}>
                            <td title={displayValue(site.client_name)}>{displayValue(site.client_name)}</td>
                            <td title={displayValue(site.site_name)}><strong>{displayValue(site.site_name)}</strong> {site.archived && <Tag>Unavailable</Tag>}</td>
                            <td>{displayValue(site.region)}</td>
                            <td>
                              <div className={styles.rowTags}>
                                <Tag color={site.archived ? "default" : "green"}>{site.archived ? "Retired" : "Active"}</Tag>
                                {site.overallocated_to_site && <Tag color="orange">Overallocated</Tag>}
                              </div>
                            </td>
                            <td>{formatArea(site.site_allocated_capacity)}</td>
                            <td>{formatArea(site.allocated_capacity)}</td>
                            <td>{site.loader_allocation_finalized ? "Yes" : "No"}</td>
                            <td>{site.staff_allocation_finalized ? "Yes" : "No"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className={styles.emptyState}>This loader has no assigned sites.</p>
                )}
              </section>

              {!readOnly && <div className={styles.actions}>
                {lockExplanation && <p>{lockExplanation}</p>}
                {!isSiteDataCurrent && sitesStatus !== "ready" && (
                  <p>Site lock status must be loaded before this assignment can be removed.</p>
                )}
                {staff.archived && (
                  <p id="staff-details-swap-unavailable">Archived staff cannot be swapped.</p>
                )}
                <div className={styles.actionButtons}>
                  <Popconfirm
                    title="Remove loader assignment?"
                    description={`Remove ${staff.name} from ${loader.vehicle_name || "this loader"} in ${setupName || "the viewed setup"}? Loader, site, plow, and Double Down allocations will remain.`}
                    okText="Remove assignment"
                    okButtonProps={{ danger: true }}
                    cancelText="Cancel"
                    onConfirm={() => onDeallocate(loader.vehicle_id, staff.staff_id)}
                    disabled={!canDeallocate}
                  >
                    <Button danger disabled={!canDeallocate} loading={isDeallocating}>
                      Remove loader assignment
                    </Button>
                  </Popconfirm>
                  <Button
                    className={styles.swapButton}
                    aria-label={`Swap ${staff.name || "staff member"} on ${loader.vehicle_name || "this loader"}`}
                    aria-describedby={staff.archived ? "staff-details-swap-unavailable" : undefined}
                    disabled={!canSwap}
                    loading={isSwapping}
                    onClick={() => onSwap({
                      ...staff,
                      vehicle_id: loader.vehicle_id,
                      vehicle_name: loader.vehicle_name,
                    })}
                  >
                    Swap
                  </Button>
                </div>
              </div>}
            </>
          )}
        </div>
      ) : null}
    </Modal>
  );
};

export { displayValue, formatArea, formatPayRate, toFiniteNumber };
export default StaffDetailsModal;
